import { Fragment, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

/**
 * Reusable Modal Component
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {string} props.title - Modal title
 * @param {React.ReactNode} props.children - Modal content
 * @param {string} props.size - Modal size: 'sm', 'md', 'lg', 'xl', '2xl', 'full' (default: 'md')
 * @param {boolean} props.showCloseButton - Show close button (default: true)
 * @param {boolean} props.closeOnBackdropClick - Close modal on backdrop click (default: true)
 * @param {string} props.overlayClassName - Additional classes for overlay
 * @param {string} props.panelClassName - Additional classes for panel
 * @param {React.ReactNode} props.footer - Custom footer content
 * @param {boolean} props.useHeadlessUI - Use HeadlessUI Dialog component (default: true)
 * @param {string} props.alignment - Modal alignment: 'center', 'top' (default: 'center')
 */
export default function Modal({
    isOpen,
    onClose,
    title,
    children,
    size = "md",
    showCloseButton = true,
    closeOnBackdropClick = true,
    overlayClassName = "",
    panelClassName = "",
    footer,
    useHeadlessUI = true,
    alignment = "center",
}) {
    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    // Size mappings
    const sizeMap = {
        sm: "max-w-sm",
        md: "max-w-md",
        lg: "max-w-lg",
        xl: "max-w-xl",
        "2xl": "max-w-2xl",
        "3xl": "max-w-3xl",
        "4xl": "max-w-4xl",
        "5xl": "max-w-5xl",
        "6xl": "max-w-6xl",
        full: "max-w-[95%]",
    };

    // Alignment mappings
    const alignmentMap = {
        center: "items-center justify-center",
        top: "items-start justify-center pt-16",
    };

    const sizeClass = sizeMap[size] || sizeMap.md;
    const alignmentClass = alignmentMap[alignment] || alignmentMap.center;

    // HeadlessUI version (with animations)
    if (useHeadlessUI) {
        return (
            <Transition appear show={isOpen} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-50"
                    onClose={closeOnBackdropClick ? onClose : () => { }}
                >
                    {/* Overlay */}
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className={`fixed inset-0 bg-black/30 ${overlayClassName}`} />
                    </Transition.Child>

                    {/* Modal Container */}
                    <div className="fixed inset-0 overflow-y-auto">
                        <div
                            className={`flex min-h-full p-6 text-center ${alignmentClass}`}
                        >
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel
                                    className={`w-full ${sizeClass} transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all relative ${panelClassName}`}
                                >
                                    {/* Header */}
                                    {(title || showCloseButton) && (
                                        <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200 -mx-6 px-6">
                                            {title && (
                                                <Dialog.Title
                                                    as="h3"
                                                    className="text-xl font-semibold text-gray-900"
                                                >
                                                    {title}
                                                </Dialog.Title>
                                            )}
                                            {showCloseButton && (
                                                <button
                                                    onClick={onClose}
                                                    className="p-2 rounded-full hover:bg-gray-100 transition-transform transform hover:rotate-90 duration-200 ml-auto"
                                                    aria-label="Close modal"
                                                >
                                                    <XMarkIcon className="w-6 h-6 text-gray-500" />
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {/* Content */}
                                    <div className="py-2">{children}</div>

                                    {/* Footer */}
                                    {footer && (
                                        <div className="border-t border-gray-200 mt-4 pt-4 -mx-6 px-6">
                                            {footer}
                                        </div>
                                    )}
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        );
    }

    // Custom div-based version (simpler, no animations)
    if (!isOpen) return null;

    return (
        <div
            className={`fixed inset-0 bg-black/30 flex ${alignmentClass} z-50 overflow-auto`}
            onClick={closeOnBackdropClick ? onClose : undefined}
        >
            <div
                className={`bg-white w-full ${sizeClass} rounded-3xl shadow-xl p-6 relative mx-4 my-4 ${panelClassName}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                {(title || showCloseButton) && (
                    <div className="flex items-center justify-between mb-6">
                        {title && <h2 className="text-2xl font-bold">{title}</h2>}
                        {showCloseButton && (
                            <button
                                onClick={onClose}
                                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-transform transform hover:rotate-90 duration-200"
                                aria-label="Close modal"
                            >
                                <XMarkIcon className="w-6 h-6 text-gray-500" />
                            </button>
                        )}
                    </div>
                )}

                {/* Content */}
                {children}

                {/* Footer */}
                {footer && (
                    <div className="border-t border-gray-200 mt-4 pt-4 -mx-6 px-6">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}

